import React from "react";
import { motion } from "motion/react";
import { Card } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Heart, Lightbulb, Target } from "lucide-react";
import TeamShowcase from "../Components/ui/team-showcase";

const founders = [
  {
    name: "Ammar Omar",
    role: "Co-Founder & Health Tech Lead",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68f7c9965d72f92ed4e7245a/9a9d2ac25_Screenshot2025-10-21at105252PM.png",
    bio: "Student passionate about Biology, Computer Science, and Computational Biology. Research intern at Moffitt Cancer Center's Integrated Mathematical Oncology program. YouTube educator with 35K+ viewers sharing advances in Bioinformatics.",
    expertise: ["Computational Biology", "Mathematical Modeling", "Java", "Python", "Scientific Writing", "Public Speaking"],
    linkedin: "https://www.linkedin.com/in/ammar-omar-64354b35b/",
    email: "ammar.a.a.omar@gmail.com"
  },
  {
    name: "Sharan Santharam",
    role: "Co-Founder & Data Science Lead",
    image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68f7c9965d72f92ed4e7245a/81710b7fd_Screenshot2025-10-01at10454AM.jpg",
    bio: "Aspiring Aerospace Engineer from Robinson High School IB. Innovation and technology enthusiast committed to using data-driven insights to promote healthier sun habits and build products that make a real impact.",
    expertise: ["Data Science", "Analytics", "Innovation", "Technology"],
    linkedin: "https://www.linkedin.com/in/sharan-santharam/",
    email: "Sharan.santharam@icloud.com"
  },
  {
    name: "Sujay Korada",
    role: "Co-Founder & Lead Engineer",
    image: "/founders/sujay-korada.png",
    bio: "Passionate about leveraging AI and computer vision to solve real-world health problems. Leading the technical development of Sunscreened's advanced skin analysis algorithms.",
    expertise: ["AI/ML", "Computer Vision", "Mobile Development"],
    linkedin: "https://www.linkedin.com/in/sujay-korada-4ab388335/",
    email: "sujaykorada@gmail.com"
  }
];

const values = [
  {
    icon: Heart,
    title: "Health First",
    description: "We believe everyone deserves access to advanced skin health monitoring, regardless of their background or resources.",
    color: "from-coral-400 to-pink-500"
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Combining cutting-edge AI, computer vision, and dermatological science to create solutions that were previously impossible.",
    color: "from-orange-400 to-amber-500"
  },
  {
    icon: Target,
    title: "Impact",
    description: "Every feature we build aims to create lasting behavioral change, helping people develop healthier relationships with sun exposure.",
    color: "from-purple-400 to-indigo-500"
  }
];

function buildTeamShowcaseMembers() {
  return founders.map((f, i) => ({
    id: String(i + 1),
    name: f.name,
    role: f.role,
    image:
      f.image ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(f.name)}&size=400&background=f97316&color=fff`,
    social: {
      linkedin: f.linkedin,
      mail: f.email ? `mailto:${f.email}` : undefined,
    },
  }));
}

export default function Founders() {
  const [theme, setTheme] = React.useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
    return 'dark';
  });
  React.useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);

    const handleStorageChange = () => {
      const newTheme = localStorage.getItem('theme');
      if (newTheme && newTheme !== theme) {
        setTheme(newTheme);
      }
    };

    const handleThemeChange = () => {
      const newTheme = localStorage.getItem('theme');
      if (newTheme && newTheme !== theme) {
        setTheme(newTheme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('themechange', handleThemeChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, [theme]);

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-[#05070d] text-slate-100" : "bg-[#fff8f3] text-slate-900"
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 py-20 text-white md:py-32">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(255,255,255,0.35),transparent_45%)]" />
        <motion.div
          className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-display text-5xl font-semibold tracking-tight md:text-6xl">Meet the founders</h1>
          <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-white/90 md:text-2xl">
            Three passionate innovators combining engineering, health technology, and AI to revolutionize sun safety
          </p>
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h2
            className={`font-display text-3xl font-semibold tracking-tight md:text-4xl ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}
          >
            The founders
          </h2>
          <p
            className={`mx-auto mt-3 max-w-2xl text-sm md:text-base ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Hover photos or names—LinkedIn and email appear on the active row.
          </p>
        </div>

        <div
          className={`rounded-3xl border p-4 md:p-8 ${
            theme === "dark"
              ? "border-white/10 bg-white/[0.03]"
              : "border-orange-100 bg-white shadow-sm"
          }`}
        >
          <TeamShowcase members={buildTeamShowcaseMembers()} />
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Our Story */}
        <div className="mb-20">
          <Card className={`border-2 overflow-hidden ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'border-gray-100'
          }`}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className={`p-12 ${
                theme === 'dark'
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900'
                  : 'bg-gradient-to-br from-orange-50 to-coral-50'
              }`}>
                <h2 className={`text-4xl font-bold mb-6 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Our Story
                </h2>
                <div className={`space-y-4 leading-relaxed ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <p>
                    Sunscreened was born from a simple observation: despite decades of research on sun damage and skin cancer, most people lack access to personalized, scientific guidance on sun protection.
                  </p>
                  <p>
                    As students passionate about engineering, health technology, and AI, we saw an opportunity to democratize advanced dermatological monitoring. By combining computer vision, real-time environmental data, and scientific measures like the Melanin Index and Erythema Index, we created a tool that brings laboratory-grade analysis to anyone with a smartphone.
                  </p>
                  <p>
                    Our mission goes beyond just building an app—we're working to raise awareness about responsible tanning and skin protection, helping people make safer choices through data visualization and personalized insights.
                  </p>
                </div>
              </div>
              <div className="relative h-full min-h-[400px]">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                  alt="Team collaboration"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-transparent" />
              </div>
            </div>
          </Card>
        </div>

        {/* Our Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className={`text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              What Drives Us
            </h2>
            <p className={`text-xl ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              The principles that guide every decision we make
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index}
                className={`p-8 border-2 ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 hover:border-orange-500'
                    : 'border-gray-100 hover:border-orange-200'
                } hover:shadow-xl transition-all duration-300`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {value.title}
                </h3>
                <p className={`leading-relaxed ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>

        {/* The Technology */}
        <div className="mb-20">
          <Card className={`border-2 p-12 ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-gradient-to-br from-gray-50 to-white'
          }`}>
            <div className="max-w-4xl mx-auto">
              <h2 className={`text-4xl font-bold mb-6 text-center ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Engineering Meets Healthcare
              </h2>
              <p className={`text-lg leading-relaxed mb-8 text-center ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Our project represents a unique intersection of multiple disciplines:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className={`p-6 rounded-xl border-2 ${
                  theme === 'dark'
                    ? 'bg-gray-900 border-orange-500/30 text-gray-200'
                    : 'bg-white border-orange-100 text-gray-600'
                }`}>
                  <h3 className={`font-bold mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Computer Vision & AI</h3>
                  <p className="text-sm leading-relaxed">
                    Advanced color analysis using the Melanin Index, Erythema Index, and Individual Typology Angle (ITA) to detect subtle skin changes invisible to the naked eye.
                  </p>
                </div>
                
                <div className={`p-6 rounded-xl border-2 ${
                  theme === 'dark'
                    ? 'bg-gray-900 border-coral-500/30 text-gray-200'
                    : 'bg-white border-coral-100 text-gray-600'
                }`}>
                  <h3 className={`font-bold mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Real-Time Data Integration</h3>
                  <p className="text-sm leading-relaxed">
                    Live weather and UV index monitoring combined with location-based recommendations for truly personalized sun protection guidance.
                  </p>
                </div>
                
                <div className={`p-6 rounded-xl border-2 ${
                  theme === 'dark'
                    ? 'bg-gray-900 border-purple-500/30 text-gray-200'
                    : 'bg-white border-purple-100 text-gray-600'
                }`}>
                  <h3 className={`font-bold mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Behavioral Science</h3>
                  <p className="text-sm leading-relaxed">
                    Data visualization and progress tracking designed to encourage lasting behavioral change and healthier sun habits.
                  </p>
                </div>
                
                <div className={`p-6 rounded-xl border-2 ${
                  theme === 'dark'
                    ? 'bg-gray-900 border-pink-500/30 text-gray-200'
                    : 'bg-white border-pink-100 text-gray-600'
                }`}>
                  <h3 className={`font-bold mb-3 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Clinical Research</h3>
                  <p className="text-sm leading-relaxed">
                    Built on established dermatological science and validated measurement techniques used in professional clinical settings.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className={`border-2 p-12 ${
            theme === 'dark'
              ? 'border-orange-500 bg-gray-800'
              : 'border-orange-200 bg-gradient-to-br from-orange-50 to-coral-50'
          }`}>
            <h2 className={`text-3xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Join Us on This Journey
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              We're committed to making sun safety accessible, personalized, and effective. Together, we can help people enjoy the sun responsibly while protecting their skin for life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-orange-500 to-coral-500 text-white hover:from-orange-600 hover:to-coral-600">
                Download Sunscreened
              </Button>
              <Button size="lg" variant="outline" className={`border-2 ${
                theme === 'dark' 
                  ? 'border-orange-500 text-orange-400 hover:bg-orange-900/20' 
                  : 'border-orange-300 text-orange-700 hover:bg-orange-50'
              }`}>
                Get in Touch
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}